import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  isErrorCodeOnlyAPIError,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { PhoneInputValue } from "@jugl-web/ui-components/cross-platform";
import { useRequestOtpMutation } from "@web-src/features/auth/authApi";
import ReCAPTCHA from "react-google-recaptcha";
import { Portal } from "@headlessui/react";
import { environment } from "@web-src/environments/environment";
import {
  getStorageItem,
  JUGL_LAST_LOGIN_METHOD,
} from "@jugl-web/utils/storage";
import { useInterval } from "react-use";
import { AuthStep, LoginMethod, PhoneMethod } from "../../types";
import { LoginPageContext, LoginPageContextValue } from "./LoginPageContext";

export const LoginPageProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);
  const [authStep, setAuthStep] = useState<AuthStep>(AuthStep.login);
  const [isLoading, setIsLoading] = useState(false);
  const lastLoginMethod = getStorageItem<LoginMethod | undefined>(
    JUGL_LAST_LOGIN_METHOD
  );
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    LoginMethod.email
  );
  const [phoneMethod, setPhoneMethod] = useState<PhoneMethod>(PhoneMethod.sms);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState<PhoneInputValue>({
    code: "",
    iso: "",
    phone: "",
    isValid: false,
  });
  const [id, setId] = useState("");

  const { toast } = useToast({ variant: "web" });
  const { t } = useTranslations();

  const [requestOtp] = useRequestOtpMutation();
  const [reCaptchaPassed, setReCaptchaPassed] = useState(
    !environment.isReCaptchaEnabled
  );
  const requestOtpVerification = useCallback(async () => {
    setIsLoading(true);
    let grcToken = "";
    try {
      if (
        environment.isReCaptchaEnabled &&
        // NOTE: don't check captcha for test phone numbers
        !(phone.phone && /^000\d{4}000$/.test(phone.phone))
      ) {
        setReCaptchaPassed(false);
        grcToken = (await recaptchaRef.current?.executeAsync()) || "";
        if (!grcToken) {
          return;
        }
        setReCaptchaPassed(true);
      }
    } catch (error) {
      setIsLoading(false);
      return;
    }
    if (loginMethod === LoginMethod.phone) {
      setEmail("");
    } else if (loginMethod === LoginMethod.email) {
      setPhone({
        code: "",
        iso: "",
        phone: "",
        isValid: false,
      });
    }
    const response = await requestOtp({
      country: phone.iso,
      country_isd_code:
        loginMethod === LoginMethod.phone ? phone.code : undefined,
      mobile: loginMethod === LoginMethod.phone ? phone.phone : undefined,
      email: loginMethod === LoginMethod.email ? email : undefined,
      otp_via: loginMethod === LoginMethod.phone ? phoneMethod : undefined,
      grc_token: grcToken,
    });
    recaptchaRef.current?.reset();
    setReCaptchaPassed(false);
    if ("error" in response && response.error) {
      if (
        isErrorCodeOnlyAPIError(response.error) &&
        response.error.data.errors === "rate_limit_reached"
      ) {
        toast(
          t({
            id: "feedback.rate-limit-reached",
            defaultMessage: "You have reached the rate limit.",
          }),
          {
            variant: "error",
          }
        );
      } else {
        toast(
          t({
            id: "feedback.something-went-wrong",
            defaultMessage: "Something went wrong.",
          }),
          {
            variant: "error",
          }
        );
      }

      setIsLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw response.error;
    }
    if ("data" in response && response.data) {
      setId(response.data.id);
    }
    setIsLoading(false);
  }, [
    email,
    loginMethod,
    phone.code,
    phone.iso,
    phone.phone,
    phoneMethod,
    requestOtp,
    t,
    toast,
  ]);

  const value: LoginPageContextValue = useMemo(
    () => ({
      authStep,
      loginMethod,
      phoneMethod,
      lastLoginMethod,
      email,
      phone,
      id,
      isLoading,
      setIsLoading,
      setAuthStep,
      setLoginMethod,
      setPhoneMethod,
      setEmail,
      setPhone,
      setId,
      requestOtpVerification,
    }),
    [
      authStep,
      loginMethod,
      phoneMethod,
      lastLoginMethod,
      email,
      phone,
      id,
      isLoading,
      setIsLoading,
      setAuthStep,
      setLoginMethod,
      setPhoneMethod,
      setEmail,
      setPhone,
      setId,
      requestOtpVerification,
    ]
  );

  const [reCaptchaIsOpen, setReCaptchaIsOpen] = useState(false);
  useInterval(
    () => {
      const iframes = document.querySelectorAll(
        'iframe[src*="recaptcha/api2/bframe"]'
      );
      if (iframes.length === 0) return;
      const recaptchaOverlay = iframes[0]?.parentNode
        ?.parentNode as HTMLElement;
      if (!recaptchaOverlay) return;
      if (isLoading && recaptchaOverlay.style.visibility === "visible")
        setReCaptchaIsOpen(true);
      if (recaptchaOverlay.style.visibility === "hidden" && reCaptchaIsOpen) {
        setReCaptchaIsOpen(false);
        if (isLoading) {
          setIsLoading(false);
        }
      }
    },
    (isLoading && !reCaptchaPassed) || reCaptchaIsOpen ? 100 : null
  );

  return (
    <LoginPageContext.Provider value={value}>
      {environment.isReCaptchaEnabled && (
        <Portal>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={environment.reCaptchaKey}
          />
        </Portal>
      )}
      {children}
    </LoginPageContext.Provider>
  );
};
